import type { ReactNode } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, tone } from './primitives';

/** A visitor's message, entering at `at` ms. */
export function VisitorBubble({
  at,
  children,
}: {
  at: number;
  children: ReactNode;
}) {
  return (
    <div className='flex justify-end'>
      <p
        className={clsxm(
          styles.bubble,
          styles.bubbleEnd,
          'max-w-[82%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2.5 dark:bg-white/[0.07]',
          tone.ink,
        )}
        style={delay(at)}
      >
        {children}
      </p>
    </div>
  );
}

/** The agent's message, in the demo business's own colour. */
export function AgentBubble({
  brand,
  children,
}: {
  /** Tailwind background class. */
  brand: string;
  children: ReactNode;
}) {
  return (
    <p
      className={clsxm(
        'max-w-[85%] rounded-2xl rounded-es-md px-4 py-2.5 text-white',
        brand,
      )}
    >
      {children}
    </p>
  );
}

/**
 * An agent reply with the typing indicator it replaces. Both sit in one grid
 * cell, so the reply's space is reserved from the start and nothing below it
 * jumps when it arrives.
 */
export function AgentReply({
  typingAt,
  at,
  children,
}: {
  typingAt?: number;
  at: number;
  children: ReactNode;
}) {
  return (
    <div className='grid'>
      {typingAt !== undefined && (
        <span
          className={clsxm(
            styles.typing,
            'flex h-9 w-14 items-center justify-center gap-1 self-start rounded-2xl rounded-es-md bg-[#f1f1f5] [grid-area:1/1] dark:bg-white/[0.07]',
          )}
          style={delay(typingAt, { '--len': `${at - typingAt}ms` })}
        >
          <span className='h-1.5 w-1.5 rounded-full bg-[#8a8aa1]' />
          <span className='h-1.5 w-1.5 rounded-full bg-[#8a8aa1]' />
          <span className='h-1.5 w-1.5 rounded-full bg-[#8a8aa1]' />
        </span>
      )}
      <div
        className={clsxm(
          styles.bubble,
          styles.bubbleStart,
          'flex min-w-0 [grid-area:1/1]',
        )}
        style={delay(at)}
      >
        {children}
      </div>
    </div>
  );
}
